import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';
import { loadStartDateTriggeredCommunications } from '../../start-date-triggered-communication/actions/load-start-date-triggered-communications';
import {
  startDateTriggeredCommunicationForOrgIdSelector,
  startDateTriggeredCommunicationsStateForOrgIdSelector,
} from '../../start-date-triggered-communication/start-date-triggered-communication-selectors';

import { issueFilters } from '../../employees/employee-list';
import { AUTOMATION_TRIGGER_TYPES } from '../../start-date-triggered-communication/automation-trigger-types';
import {
  formattedSendInterval,
  getAutomationsByInterval,
  getIntervalForAutomations,
  sortStartDateTriggeredCommunications,
} from '../../start-date-triggered-communication/start-date-triggered-communication-utils';

import { AutomationSectionEmptyState } from './widgets/automation-section-empty-state';
import { AutomationTimelineTitleCreate } from './widgets/automation-timeline-title-create';
import { AutomationTimelineTriggerSection } from './widgets/automation-timeline-trigger-section';
import { AutomationWarning } from './widgets/automation-warning';
import { LoadingMessage } from '../../widgets/loading-message';
import { calculateEmployeePercentage } from '../utils/automation-warning-utils';

const mapStateToProps = (state: any, { org: { org_id: orgId } }: InputProps) => {
  const automations = startDateTriggeredCommunicationForOrgIdSelector(state, orgId) || [];
  return {
    birthdayAutomations: automations.filter(
      (automation) => automation.triggerType === AUTOMATION_TRIGGER_TYPES.BIRTHDATE,
    ),
    birthdayAutomationsState: startDateTriggeredCommunicationsStateForOrgIdSelector(state, orgId),
    employees: employeesForOrgIdSelector(state, orgId),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { org: { org_id: orgId } }: InputProps,
) => ({
  actions: {
    loadStartDateTriggeredCommunications: () =>
      dispatch(loadStartDateTriggeredCommunications(orgId)),
    loadEmployees: () => dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT)),
  },
});

type InputProps = { org: Org };
type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const BirthdayAutomations = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ actions, org, birthdayAutomations, birthdayAutomationsState, employees }: Props) => {
  useEffect(() => {
    actions.loadStartDateTriggeredCommunications();
    actions.loadEmployees();
  });

  if (!birthdayAutomationsState || birthdayAutomationsState.loading) {
    return <LoadingMessage />;
  }

  const section = 'birthday';
  if (!birthdayAutomations || !birthdayAutomations.length) {
    return <AutomationSectionEmptyState section={section} org={org} />;
  }

  const sortedAutomations = sortStartDateTriggeredCommunications(birthdayAutomations);
  const automationsByInterval = getAutomationsByInterval(sortedAutomations);

  const currentEmployees = employees.filter((e) => e.isCurrentEmployee);
  const employeesMissingBirthdate: Employee[] =
    issueFilters.missingBirthdate.filter(currentEmployees);
  const link: string = issueFilters.missingBirthdate.link(org.org_id);
  const { percentNotInCategory: employeesBirthdatePercent } = calculateEmployeePercentage(
    employeesMissingBirthdate.length,
    currentEmployees.length,
  );

  return (
    <div className="u-display-flex u-flexDirection-column u-width-100">
      {employeesMissingBirthdate.length > 0 && (
        <AutomationWarning
          missingFieldIssue={{
            object: 'employees',
            field: 'a birthdate',
            percentCorrect: employeesBirthdatePercent,
          }}
          link={link}
          variant="employeeIssue"
        />
      )}
      {automationsByInterval.map((automationsForInterval, index) => (
        <AutomationTimelineTriggerSection
          key={`${section}-${formattedSendInterval(automationsForInterval)}`}
          org={org}
          trigger={AUTOMATION_TRIGGER_TYPES.BIRTHDATE}
          title={formattedSendInterval(automationsForInterval)}
          isFirst={index === 0}
          automations={automationsForInterval.automations}
          interval={getIntervalForAutomations(automationsForInterval)}
        />
      ))}
      <AutomationTimelineTitleCreate
        org={org}
        triggerType={AUTOMATION_TRIGGER_TYPES.BIRTHDATE}
        isLast
      />
    </div>
  );
});
